import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { ValidatorForm } from "react-material-ui-form-validator";
import PageHeaderTabs from "../page-header-tabs";
import { Grid, CircularProgress, Typography, Box } from "@material-ui/core";
import ReactGA from "react-ga";
import {
	FormActions,
	FormButton,
	FormSubmitButton,
	Page,
	PageContent,
	ScrollToTopOnMount,
	Feedback,
} from "frame/components";
import {
	strip,
	SelectHazardsForm,
	PeopleForm,
	SiteForm,
	UploadFormCard,
	ExtraNotesForm,
	EmailRecipientsForm,
} from "features/forms";
import { types as fromHealthAndSafety } from "features/health-and-safety";
import {
	AddHazardToSiteForm,
	AddHazardousSubstanceToSiteForm,
} from "features/health-safety-forms";
import {
	useRouter,
	isSameLength,
	isNotNullOrEmpty,
	isDefaultGuid,
	validateAndScrollTo,
	isProd,
} from "utils";
import { useSelector, useDispatch } from "react-redux";
import { onHazardListRequest, onHazardousSubstanceListRequest } from "features/health-and-safety/actions";

const useStyles = makeStyles((theme) => ({
	body: {
		marginTop: theme.spacing(-2),
	},
	actions: {
		paddingBottom: theme.spacing(8),
	},
}));

const Form = ({ values, onChange, onReset, onSave, onLookup, currentSite }) => {
	const classes = useStyles();
	const { history } = useRouter();
	const dispatch = useDispatch();

	const guidelinesBlockRef = useRef();

	const [selectedSite, setSelectedSite] = useState(null);

	const [showReadGuidelinesPrompt, setShowReadGuidelinesPrompt] =
		useState(false);

	const hazardsState = useSelector(
		({ healthSafety }) => healthSafety.hazards
	);
	const hazardousSubstancesState = useSelector(
		({ healthSafety }) => healthSafety.hazardousSubstances
	);
	const currentOrganisation = useSelector(
		({ organisations }) => organisations.currentOrganisation
	);

	const hazardList = hazardsState?.results;
	const hazardousSubstanceList = hazardousSubstancesState?.results;
	const siteList = useSelector(({ sites }) => sites.pagination?.results);

	// Check if hazards and hazardous substances are loaded
	const hazardsLoaded = hazardsState?.flag === "done" && Array.isArray(hazardList);
	const hazardousSubstancesLoaded = hazardousSubstancesState?.flag === "done" && Array.isArray(hazardousSubstanceList);
	const isLoadingHazards = hazardsState?.flag === "processing";
	const isLoadingHazardousSubstances = hazardousSubstancesState?.flag === "processing";
	
	// Determine if we're still waiting for data (either loading or not yet requested)
	// Data is loading if: currently processing, or hasn't been loaded and hasn't failed
	const isHazardsPending = isLoadingHazards || (!hazardsLoaded && hazardsState?.flag !== "error" && hazardsState?.flag !== "done");
	const isHazardousSubstancesPending = isLoadingHazardousSubstances || (!hazardousSubstancesLoaded && hazardousSubstancesState?.flag !== "error" && hazardousSubstancesState?.flag !== "done");
	const isDataLoading = isHazardsPending || isHazardousSubstancesPending;

	// Ensure hazard and hazardous substance data is loaded on mount or when organisation changes
	useEffect(() => {
		if (!currentOrganisation) {
			return;
		}

		const hazardsFlag = hazardsState?.flag;
		const hazardousSubstancesFlag = hazardousSubstancesState?.flag;
		const hazardsResults = hazardsState?.results;
		const hazardousSubstancesResults = hazardousSubstancesState?.results;

		// Check if hazards are actually loaded (flag is "done" and we have results array)
		const areHazardsLoaded = hazardsFlag === "done" && Array.isArray(hazardsResults);
		// Check if hazardous substances are actually loaded (flag is "done" and we have results array)
		const areHazardousSubstancesLoaded = hazardousSubstancesFlag === "done" && Array.isArray(hazardousSubstancesResults);

		// Request hazards if not already loaded, not currently loading, and not in error state
		if (!areHazardsLoaded && hazardsFlag !== "processing" && hazardsFlag !== "error") {
			dispatch(onHazardListRequest());
		}
		
		// Request hazardous substances if not already loaded, not currently loading, and not in error state
		if (!areHazardousSubstancesLoaded && hazardousSubstancesFlag !== "processing" && hazardousSubstancesFlag !== "error") {
			dispatch(onHazardousSubstanceListRequest());
		}
		// Depend on currentOrganisation to trigger when org is set/changes
		// Also depend on state flags to re-check when data loading completes
		// The guards inside prevent unnecessary dispatches
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOrganisation, hazardsState?.flag, hazardousSubstancesState?.flag]);

	useEffect(() => {
		// called on every site change in currentSite.hazards & currentSite.substances
		async function fetchSiteData(id) {
			const siteData = siteList && siteList.filter((s) => s.id === id)[0];
			let siteResponseData = siteData ? siteData : null;

			// to fix the order of site hazards when returned from api, push the newly added hazard to the end of array
			if (
				siteResponseData &&
				values.hazards &&
				values.hazards.length > 0
			) {
				// to ignore on load run, triggered after adding new hazard

				const currentlyReadHazards = values.hazards || [];
				const newlyAddedHazard = siteResponseData.hazards.filter(
					(h) => !currentlyReadHazards.includes(h.hazardId)
				)[0];

				// removed newlyAddedHazard from array
				if (newlyAddedHazard) {
					let siteResponseHazardsData =
						siteResponseData.hazards.filter(
							(h) => h.hazardId !== newlyAddedHazard.hazardId
						);

					siteResponseHazardsData.push(newlyAddedHazard); // push newlyAddedHazard to end of array
					siteResponseData.hazards = siteResponseHazardsData; // update site hazards
				}
			}

			// to fix the order of site substances when returned from api, push the newly added substance to the bottom
			if (
				siteResponseData &&
				values.substances &&
				values.substances.length > 0
			) {
				// to ignore on load run, triggered after adding new substance

				const currentlyReadSubstances = values.substances || [];
				const newlyAddedSubstance = siteResponseData.substances.filter(
					(hs) =>
						!currentlyReadSubstances.includes(
							hs.hazardousSubstanceId
						)
				)[0];

				// removed newlyAddedSubstance from array
				if (newlyAddedSubstance) {
					let siteResponseSubstancesData =
						siteResponseData.substances.filter(
							(hs) =>
								hs.hazardousSubstanceId !==
								newlyAddedSubstance.hazardousSubstanceId
						);

					siteResponseSubstancesData.push(newlyAddedSubstance); // push newlyAddedSubstance to end of array
					siteResponseData.substances = siteResponseSubstancesData; // update site substances
				}
			}

			setSelectedSite(siteResponseData);
		}
		if (values.siteId && !isDefaultGuid(values.siteId)) {
			fetchSiteData(values.siteId);
		} // eslint-disable-next-line
	}, [currentSite.hazards, currentSite.substances, values.siteId]);

	useEffect(() => {
		// lookup site by id and set result as currentSite
		if (values.siteId) {
			onLookup(values.siteId);
		}
	}, [onLookup, values.siteId]);

	const onSubmit = (event) => {
		event.preventDefault();

		// Set error state first to ensure message displays
		if (!validGuidelinesRule) {
			setShowReadGuidelinesPrompt(true);
		}

		if (
			validateAndScrollTo(
				validGuidelinesRule,
				guidelinesBlockRef,
				setShowReadGuidelinesPrompt
			)
		) {
			isProd &&
				ReactGA.event({
					category: "Tasks",
					action: "Save Clicked",
					label: "Add Induction Meeting",
				});
			onSave(strip(values));
		}
	};

	const onCancel = () => {
		onReset();
		history.push(fromHealthAndSafety.TASK_INDUCTION_MINUTES_ROUTE);
	};

	const siteHazards =
		selectedSite &&
		selectedSite.hazards &&
		selectedSite.hazards.filter((node) => node.status !== "Archived");

	const siteSubstances =
		selectedSite &&
		selectedSite.substances &&
		selectedSite.substances.filter((node) => node.status !== "Archived");

	// Check validation separately to provide specific error messages
	// If no site hazards/substances exist, validation passes (nothing to check)
	const hasSiteHazards = siteHazards && siteHazards.length > 0;
	const hasSiteSubstances = siteSubstances && siteSubstances.length > 0;
	const hazardsValid = !hasSiteHazards || isSameLength(values.hazards, siteHazards);
	const substancesValid = !hasSiteSubstances || isSameLength(values.substances, siteSubstances);
	const validGuidelinesRule = hazardsValid && substancesValid;

	// Generate specific error message based on what's missing
	const getValidationErrorMessage = () => {
		if (!showReadGuidelinesPrompt) {
			return "";
		}

		const hazardsChecked = values.hazards ? values.hazards.length : 0;
		const substancesChecked = values.substances ? values.substances.length : 0;

		if (!hazardsValid && !substancesValid) {
			return "Please read all hazard and hazardous substance guidelines. Check the boxes next to each item in both sections to confirm you have read the guidelines.";
		} else if (!hazardsValid) {
			const remaining = hasSiteHazards ? siteHazards.length - hazardsChecked : 0;
			return `Please read all hazard guidelines. You need to check ${remaining} more hazard${remaining !== 1 ? "s" : ""} before saving.`;
		} else if (!substancesValid) {
			const remaining = hasSiteSubstances ? siteSubstances.length - substancesChecked : 0;
			return `Please read all hazardous substance guidelines. You need to check ${remaining} more hazardous substance${remaining !== 1 ? "s" : ""} before saving.`;
		}

		return "Please read all guidelines before saving.";
	};

	useEffect(() => {
		if (validGuidelinesRule) {
			setShowReadGuidelinesPrompt(false);
		}
		// eslint-disable-next-line
	}, [values.hazards, values.substances]);

	// use to compare which entries are preExisting to know which to hide on "add hazard to site" form load
	const preExistingSiteHazards = isNotNullOrEmpty(siteHazards)
		? siteHazards.map((s) => s.hazardId)
		: [];
	const preExistingSiteSubstances = isNotNullOrEmpty(siteSubstances)
		? siteSubstances.map((s) => s.hazardousSubstanceId)
		: [];

	return (
		<Page title="+ Induction Meeting">
			<ScrollToTopOnMount />
			<PageHeaderTabs
				// breadcrumbs="Induction"
				title="+ Induction Meeting"
				// addTabTitle="Add induction meeting"
				// listTabTitle="View library of induction meetings"
				hideAddTabButton={true}
				listTabPath={fromHealthAndSafety.TASK_INDUCTION_MINUTES_ROUTE}
				backActionPath={fromHealthAndSafety.TASK_INDUCTION_MINUTES_ROUTE}
			/>
			<PageContent padSides padBottom padTop>
				<ValidatorForm
					autoComplete="off"
					noValidate
					onSubmit={onSubmit}
				>
					<Grid container spacing={3}>
						<Grid item lg={6} md={12} sm={12} xs={12}>
							<div className={classes.body}>
								<SiteForm values={values} onChange={onChange} />
								{values.siteId && (
									<>
										<PeopleForm
											recordName="induction"
											defaultStaffRole="Staff"
											values={values}
											onChange={onChange}
										/>
										<div ref={guidelinesBlockRef}>
											<Feedback
												active={showReadGuidelinesPrompt}
												color="error"
												disableTimeout={true}
											>
												{getValidationErrorMessage()}
											</Feedback>
										</div>
										{isDataLoading ? (
											<Box
												display="flex"
												flexDirection="column"
												alignItems="center"
												justifyContent="center"
												style={{ minHeight: 200, padding: 20 }}
											>
												<CircularProgress />
												<Typography
													variant="body2"
													style={{ marginTop: 16 }}
												>
													Loading hazard data...
												</Typography>
											</Box>
										) : (
											<SelectHazardsForm
												showNotesReminderCheckbox
												values={values}
												onChange={onChange}
												siteHazards={siteHazards}
												siteSubstances={siteSubstances}
												hazardList={hazardList}
												hazardousSubstanceList={
													hazardousSubstanceList
												}
												showError={showReadGuidelinesPrompt}
											/>
										)}
										<UploadFormCard
											title={"Add Attachments"}
											values={values}
											onChange={onChange}
										/>
										<ExtraNotesForm
											values={values}
											onChange={onChange}
										/>
										<EmailRecipientsForm
											values={values}
											onChange={onChange}
										/>
									</>
								)}
							</div>
						</Grid>
					</Grid>

					{/* debugging purposes:
            <pre>
            {JSON.stringify({
              "read all hazards guidelines" : isSameLength(values.hazards, siteHazards),
              "read all substances guidelines": isSameLength(values.substances, siteSubstances),
              'values.substances': values.substances,
              siteSubstances,
              "hazards checked": values.hazards && values.hazards.length,
              "total hazards": siteHazards && siteHazards.length,
              "substances checked": values.substances && values.substances.length,
              "total substances": siteSubstances && siteSubstances.length,
            }, null, 2)}
          </pre> */}
					<FormActions className={classes.actions}>
						<FormSubmitButton
							disabled={values.locked}
							loading={values.save.saving}
						>
							Save
						</FormSubmitButton>
						<FormButton
							disabled={values.save.saving}
							onClick={onCancel}
						>
							Cancel
						</FormButton>
					</FormActions>
				</ValidatorForm>
			</PageContent>
			{values.siteId && (
				<AddHazardToSiteForm
					siteId={values.siteId}
					preExistingSiteHazards={preExistingSiteHazards}
				/>
			)}
			{values.siteId && (
				<AddHazardousSubstanceToSiteForm
					siteId={values.siteId}
					preExistingSiteSubstances={preExistingSiteSubstances}
				/>
			)}
		</Page>
	);
};

Form.propTypes = {
	values: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
	onReset: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	onLookup: PropTypes.func.isRequired,
	currentSite: PropTypes.object.isRequired,
};

export default Form;
